// src/app/modules/productos/productos.component.ts

import { Component, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ToastrService } from 'ngx-toastr';
import { GenericTableHandlerService } from '../shared/GenericTableHandlerService.service';
import { ProductosService } from '../services/productos.service';
import { ProductoDialogComponent } from './product-dialog/product-dialog.component';
import { Sort } from '@angular/material/sort';
import { AuthService } from '../services/auth.service';
import { take } from 'rxjs/operators';
import { TableAction } from '../table-generic/table-generic.component';
import { TipoProducto, TIPO_PRODUCTO_LABELS } from '../enums/tipo-producto.enum';

@Component({
  selector: 'app-productos',
  templateUrl: './productos.component.html',
  styleUrls: ['./productos.component.scss']
})
export class ProductosComponent extends GenericTableHandlerService implements OnInit {
  isVendedor = false;
  TipoProducto = TipoProducto;
  tiposProductoFiltro = [
    { value: null, label: 'Todos' },
    { value: TipoProducto.ESTANDAR, label: TIPO_PRODUCTO_LABELS[TipoProducto.ESTANDAR] },
    { value: TipoProducto.IMPRESION_3D, label: TIPO_PRODUCTO_LABELS[TipoProducto.IMPRESION_3D] },
    { value: TipoProducto.TE, label: TIPO_PRODUCTO_LABELS[TipoProducto.TE] },
  ];
  tipoProductoSeleccionado: TipoProducto | null = null;

  constructor(
    protected productosService: ProductosService,
    dialog: MatDialog,
    toastr: ToastrService,
    private authService: AuthService
  ) {
    super(productosService, dialog, toastr);
  }

  ngOnInit(): void {
    // Verificar primero si el usuario ya está disponible en el snapshot
    const currentUser = this.authService.getCurrentUserSnapshot();
    
    if (currentUser?.['rol']) {
      // Usuario ya disponible, verificar rol y inicializar
      const rolNombre = typeof currentUser['rol'] === 'string' 
        ? currentUser['rol'] 
        : currentUser['rol']?.['rol'];
      
      this.isVendedor = rolNombre === 'Vendedor';
      this.initializeTable();
    } else {
      // Usuario no disponible aún, esperar a que esté disponible
      this.authService.currentUser$.pipe(take(1)).subscribe(user => {
        // Verificar el rol del usuario
        if (user?.['rol']) {
          const rolNombre = typeof user['rol'] === 'string' 
            ? user['rol'] 
            : user['rol']?.['rol'];
          
          this.isVendedor = rolNombre === 'Vendedor';
        }
        
        // Inicializar la tabla con las acciones correctas
        this.initializeTable();
      });
    }

    // También suscribirse a cambios del usuario para actualizar el rol
    this.authService.currentUser$.subscribe(user => {
      if (user?.['rol']) {
        const rolNombre = typeof user['rol'] === 'string' 
          ? user['rol'] 
          : user['rol']?.['rol'];
        
        this.isVendedor = rolNombre === 'Vendedor';
      }
    });
  }

  private initializeTable(): void {
    // Construir acciones dinámicamente según el rol
    const acciones: TableAction[] = [];

    // Si es vendedor, sólo se muestra el botón de detalles
    if (this.isVendedor) {
      acciones.push(
        { icon: 'visibility', tooltip: 'Ver detalles', emit: 'detailsClick' }
      );
    } else {
      // Si NO es vendedor, mostrar sólo editar y eliminar
      acciones.push(
        { icon: 'edit', tooltip: 'Editar producto', emit: 'editClick' },
        { icon: 'delete', tooltip: 'Eliminar producto', color: 'warn', emit: 'deleteClick' }
      );
    }

    this.dialogComponent = ProductoDialogComponent;
    this.initialize(
      {
        title: 'Lista de Productos',
        selectable: true,
        stickyHeader: true,
        pageSize: 10,
        pageSizeOptions: [5, 10, 25, 100],
        noDataMessage: 'No hay productos registrados',
        columns: [
          { key: 'codigo', title: 'Código', sortable: true },
          { key: 'nombre', title: 'Nombre', sortable: true },
          { key: 'precio', title: 'Precio', type: 'currency', sortable: true, align: 'left' },
          { key: 'stock', title: 'Stock', type: 'number', sortable: true, align: 'left' },
          { key: 'etiquetas', title: 'Etiquetas', sortable: false, type: 'chips' },
          {
            key: 'actions',
            title: 'Acciones',
            type: 'actions',
            actions: acciones
          }
        ]
      },
      null,
      ''// 'v1/productos'
    );

    this.loadData();
  }

  override loadData(extraParams: Record<string, any> = {}): void {
    const params = { ...extraParams };
    if (this.tipoProductoSeleccionado) {
      params['tipoProducto'] = this.tipoProductoSeleccionado;
    }
    const trimmed = this.searchTerm.trim();
    if (trimmed.length >= 2) {
      params['search'] = trimmed;
    }
    super.loadData(params);
  }

  filtrarPorTipo(tipo: TipoProducto | null): void {
    this.tipoProductoSeleccionado = tipo;
    this.currentPage$.next(0);
    this.loadData();
  }

  get currentPageIndex(): number {
    return this.currentPage$.value;
  }

  get currentPageSize(): number {
    return this.pageSize$.value;
  }

  onViewDetails(producto: any): void {
    this.abrirDialogoProducto(producto);
  }

  onEdit(item: any): void {
    console.log('Editar producto', item);
    this.abrirDialogoProducto(item);
  }

  abrirDialogoProducto(producto?: any): void {
    const dialogRef = this.dialog.open(ProductoDialogComponent, {
      width: '1000px',
      data: {
        producto: producto ? {...producto} : undefined,
        isEdit: producto && !this.isVendedor, // Solo permitir editar si no es vendedor
        isVendedor: this.isVendedor // Pasar el rol al diálogo
      }
    });

    dialogRef.afterClosed().subscribe((success: any) => {
      if (success) {
        this.loadData(); // Recargar datos solo si fue exitoso
      }
    });
  }

  private checkUserRole(): void {
    // Verificar primero si el usuario ya está disponible en el snapshot
    const currentUser = this.authService.getCurrentUserSnapshot();
    
    if (currentUser?.['rol']) {
      // Usuario ya disponible, verificar rol
      const rolNombre = typeof currentUser['rol'] === 'string' 
        ? currentUser['rol'] 
        : currentUser['rol']?.['rol'];
      
      this.isVendedor = rolNombre === 'Vendedor';
    } else {
      // Usuario no disponible aún, esperar a que esté disponible
      this.authService.currentUser$.pipe(take(1)).subscribe(user => {
        if (user?.['rol']) {
          const rolNombre = typeof user['rol'] === 'string' 
            ? user['rol'] 
            : user['rol']?.['rol'];
          
          this.isVendedor = rolNombre === 'Vendedor';
        }
      });
    }

    // También suscribirse a cambios del usuario para actualizar el rol
    this.authService.currentUser$.subscribe(user => {
      if (user?.['rol']) {
        const rolNombre = typeof user['rol'] === 'string' 
          ? user['rol'] 
          : user['rol']?.['rol'];
        
        this.isVendedor = rolNombre === 'Vendedor';
      }
    });
  }

  onDelete(item: any): void {
    if (confirm('¿Seguro que deseas eliminar este producto?')) {
      this.deleteItem(item.id).subscribe();
    }
  }

  onSearch(): void {
    const trimmed = this.searchTerm.trim();
    if (trimmed.length === 0 || trimmed.length >= 2) {
      this.currentPage$.next(0);
      this.loadData();
    }
  }

  // Modifica loadData para considerar la búsqueda
  // loadData(): void {
  //   if (this.searchTerm && this.searchTerm.trim().length >= 2) {
  //     this.onSearch();
  //   } else {
  //     super.loadData();
  //   }
  // }
}
